import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/PhpAuthContext';
import { useData } from '../contexts/DataContext';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/use-toast';
import { apiRequest } from '../config/api';
import { 
  Users, 
  Settings, 
  BarChart3, 
  FileText,
  LogOut,
  Stethoscope,
  Calendar,
  ClipboardList,
  Clock,
  DollarSign
} from 'lucide-react';

import AdminOverviewTab from '../components/admin/AdminOverviewTab';
import AdminDoctorsTab from '../components/admin/AdminDoctorsTab';
import AdminPatientsTab from '../components/admin/AdminPatientsTab';
import AppointmentsTab from '../components/admin/AppointmentsTab';
import AdminSubmissionsTab from '../components/admin/AdminSubmissionsTab';
import AdminSettingsTab from '../components/admin/AdminSettingsTab';
import AdminRevenueTab from '../components/admin/AdminRevenueTab';
import CampSubmissions from '../components/doctor/CampSubmissions';
import Attendance from '../components/doctor/Attendance';

const AdminDashboard = () => {
  const { profile, signOut } = useAuth();
  const { submissions, doctors, patients, addDoctor, updateDoctor, fetchSubmissions, fetchDoctors, fetchPatients } = useData();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [campSubmissionsInitialTab, setCampSubmissionsInitialTab] = useState('form');
  const [attendanceInitialTab, setAttendanceInitialTab] = useState('checkin');
  const [attendanceKey, setAttendanceKey] = useState(0);
  const [appointmentsCount, setAppointmentsCount] = useState(0);
  const [submissionsCount, setSubmissionsCount] = useState(0);
  const [campSubmissionsCount, setCampSubmissionsCount] = useState(0);
  const [attendanceCount, setAttendanceCount] = useState(0);
  const [leaveRequestsCount, setLeaveRequestsCount] = useState(0);
  const [leaveRequestsPendingCount, setLeaveRequestsPendingCount] = useState(0);
  const [leaveRequestsApprovedCount, setLeaveRequestsApprovedCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    fetchSubmissions();
    fetchDoctors();
    // Only fetch patients if user has permission
    if (profile && (profile.role === 'admin' || profile.role === 'doctor' || profile.role === 'chief_doctor' || profile.role === 'clinic_login')) {
      fetchPatients();
      fetchAppointmentsCount();
      fetchSubmissionsCount();
      fetchCampSubmissionsCount();
      fetchAttendanceCount();
      fetchLeaveRequestsCount();
      fetchAppointments();
    }
  }, [fetchSubmissions, fetchDoctors, fetchPatients, profile]);

  // Update current date/time every second (IST)
  useEffect(() => {
    const updateDateTime = () => {
      setCurrentDateTime(new Date());
    };
    
    updateDateTime(); // Initial update
    const timer = setInterval(updateDateTime, 1000);
    
    return () => clearInterval(timer);
  }, []);

  const fetchAppointmentsCount = async () => {
    try {
      const data = await apiRequest('get_appointments_count.php');
      if (data.success) {
        setAppointmentsCount(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching appointments count:', error);
      setAppointmentsCount(0);
    }
  };

  const fetchSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_submissions_count.php');
      if (data.success) {
        setSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch submissions count:', data.error);
        setSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching submissions count:', error);
      setSubmissionsCount(0);
    }
  };

  const fetchCampSubmissionsCount = async () => {
    try {
      const data = await apiRequest('get_camp_submissions.php?limit=1&offset=0');
      if (data.success) {
        setCampSubmissionsCount(data.total || 0);
      } else {
        console.error('Failed to fetch camp submissions count:', data.error);
        setCampSubmissionsCount(0);
      }
    } catch (error) {
      console.error('Error fetching camp submissions count:', error);
      setCampSubmissionsCount(0);
    }
  };

  const fetchAttendanceCount = async () => {
    try {
      const data = await apiRequest('get_attendance.php?limit=1&offset=0');
      if (data.success) {
        setAttendanceCount(data.total || 0);
      } else {
        console.error('Failed to fetch attendance count:', data.error);
        setAttendanceCount(0);
      }
    } catch (error) {
      console.error('Error fetching attendance count:', error);
      setAttendanceCount(0);
    }
  };

  const fetchLeaveRequestsCount = async () => {
    try {
      const data = await apiRequest('get_leave_requests.php?limit=1000&offset=0');
      if (data.success) {
        const leaveRequests = data.leave_requests || [];
        setLeaveRequestsCount(leaveRequests.length);
        // Case-insensitive status comparison
        const pending = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'pending').length;
        const approved = leaveRequests.filter(lr => (lr.status || '').toLowerCase() === 'approved').length;
        setLeaveRequestsPendingCount(pending);
        setLeaveRequestsApprovedCount(approved);
      } else {
        console.error('Failed to fetch leave requests count:', data.error);
        setLeaveRequestsCount(0);
        setLeaveRequestsPendingCount(0);
        setLeaveRequestsApprovedCount(0);
      }
    } catch (error) {
      console.error('Error fetching leave requests count:', error);
      setLeaveRequestsCount(0);
      setLeaveRequestsPendingCount(0);
      setLeaveRequestsApprovedCount(0);
    }
  };

  const fetchAppointments = async () => {
    try {
      const data = await apiRequest('get_appointments_list.php');
      if (data.success) {
        setAppointments(data.appointments || []);
      } else {
        console.error('Failed to fetch appointments:', data.error);
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
    }
  };

  // Refresh function to be passed to child components
  const refreshData = () => {
    fetchSubmissions();
    fetchSubmissionsCount();
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
    toast({
      title: 'Logged out successfully',
      description: 'See you next time!'
    });
  };

  const getSubmissionStats = () => {
    const total = submissionsCount || submissions.length;
    const inProgress = submissions.filter(s => s.status === 'in-progress').length;
    const completed = submissions.filter(s => s.status === 'completed').length;
    
    // Count only active doctors (status === 'Active')
    const activeDoctorsCount = doctors.filter(doctor => {
      const status = doctor.status;
      // Only count doctors with status === 'Active'
      // If status is not available, check is_active as fallback
      if (status !== undefined && status !== null) {
        return status === 'Active';
      }
      // Fallback to is_active if status field doesn't exist
      return doctor.is_active !== false;
    }).length;
    
    const totalDoctorsCount = doctors.length;
    
    return { 
      total, 
      inProgress, 
      completed, 
      activeDoctors: activeDoctorsCount,
      activeDoctorsDisplay: `${activeDoctorsCount}(${totalDoctorsCount})`, // Format: "5(10)"
      totalDoctors: totalDoctorsCount,
      totalPatients: patients.length,
      totalAppointments: appointmentsCount,
      campSubmissions: campSubmissionsCount
    };
  };

  const stats = getSubmissionStats();

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'doctors', label: 'Doctors', icon: Users },
    { id: 'patients', label: 'Patients', icon: Stethoscope },
    { id: 'appointments', label: 'Appointments', icon: Calendar },
    { id: 'submissions', label: 'Appointment History', icon: FileText },
    { id: 'revenue', label: 'Revenue', icon: DollarSign },
    { id: 'camp_submissions', label: 'Camp Submissions', icon: ClipboardList },
    { id: 'attendance', label: 'Attendance', icon: Clock },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const handleTabChange = (tabId, subTab = null) => {
    // Handle attendance tab navigation first (before setting activeTab)
    if (tabId === 'attendance') {
      if (subTab) {
        // Set the specific sub-tab (e.g., 'approve_leave')
        setAttendanceInitialTab(subTab);
        // Force remount by updating key if already on attendance tab
        if (activeTab === 'attendance') {
          setAttendanceKey(prev => prev + 1);
        }
      } else if (activeTab === 'overview') {
        // Default to checkin when navigating from overview without subTab
        setAttendanceInitialTab('checkin');
      }
    } else if (tabId !== 'attendance') {
      // Reset to 'checkin' when navigating away from attendance
      setAttendanceInitialTab('checkin');
    }
    
    // If navigating to camp_submissions from overview (via count card), set initial tab to 'list'
    if (tabId === 'camp_submissions' && activeTab === 'overview') {
      setCampSubmissionsInitialTab('list');
    } else if (tabId !== 'camp_submissions') {
      // Reset to 'form' when navigating away
      setCampSubmissionsInitialTab('form');
    }
    
    // Set active tab last to ensure initialTab is set first
    setActiveTab(tabId);
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Gugan Dental Care</title>
        <meta name="description" content="Manage your dental practice with comprehensive admin controls for doctors, patients, and system settings." />
      </Helmet>
      
      <div className="min-h-screen">
        {/* Header */}
        <header className="glass-effect border-b border-gray-700">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              <div className="flex items-center space-x-1 sm:space-x-2">
                <img 
                  src="/logo.png" 
                  alt="Gugan Dental Care Logo" 
                  className="h-8 sm:h-10 md:h-12 w-auto object-contain"
                  onError={(e) => {
                    const fallback = e.target.nextElementSibling;
                    if (fallback) {
                      e.target.style.display = 'none';
                      fallback.style.display = 'block';
                    }
                  }}
                />
                <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-blue-400 hidden" />
                <span className="text-base sm:text-xl font-bold gradient-text">Gugan Dental Care</span>
              </div>
              
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-white text-xs sm:text-sm">Admin: {profile?.full_name || profile?.name}</span>
                  {profile?.doctor_unique_id && (
                    <span className="text-gray-400 text-xs mt-0.5">Admin Unique ID: {profile.doctor_unique_id}</span>
                  )}
                </div>
                <span className="sm:hidden text-white text-xs truncate max-w-[120px]">Admin: {profile?.full_name || profile?.name}</span>
                <Button 
                  variant="ghost" 
                  onClick={handleLogout}
                  className="text-white hover:text-red-400 p-2 sm:px-3 sm:py-2"
                  size="sm"
                >
                  <LogOut className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
          {/* Welcome Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold gradient-text mb-1 sm:mb-2">
                  Admin Dashboard
                </h1>
                <p className="text-gray-300 text-sm sm:text-base md:text-lg">
                  Manage your dental practice and system settings
                </p>
              </div>
              <div className="text-right">
                <div className="p-3 bg-gray-800 rounded-lg border border-gray-700">
                  <p className="text-gray-400 text-xs mb-1">Current Date & Time (IST)</p>
                  <p className="text-white font-semibold text-sm sm:text-base">
                    {currentDateTime.toLocaleDateString("en-IN", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "2-digit",
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                  <p className="text-blue-400 font-mono text-lg sm:text-xl font-bold">
                    {currentDateTime.toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12: true,
                      timeZone: "Asia/Kolkata",
                    })}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-4 sm:mb-6 md:mb-8"
          >
            <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-md transition-colors whitespace-nowrap flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  <span className="text-xs sm:text-sm">{tab.label}</span>
                </button>
              ))}
            </div>
          </motion.div>

          {/* Tab Content */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            {activeTab === 'overview' && <AdminOverviewTab stats={stats} submissions={submissions} patients={patients} appointments={appointments} doctors={doctors} appointmentsCount={appointmentsCount} campSubmissionsCount={campSubmissionsCount} attendanceCount={attendanceCount} leaveRequestsCount={leaveRequestsCount} leaveRequestsPendingCount={leaveRequestsPendingCount} leaveRequestsApprovedCount={leaveRequestsApprovedCount} onNavigate={handleTabChange} />}
            {activeTab === 'doctors' && <AdminDoctorsTab doctors={doctors} addDoctor={addDoctor} updateDoctor={updateDoctor} />}
            {activeTab === 'patients' && <AdminPatientsTab onSubmissionComplete={refreshData} />}
            {activeTab === 'appointments' && <AppointmentsTab />}
            {activeTab === 'submissions' && <AdminSubmissionsTab submissions={submissions} />}
            {activeTab === 'revenue' && <AdminRevenueTab patients={patients} doctors={doctors} />}
            {activeTab === 'camp_submissions' && <CampSubmissions initialTab={campSubmissionsInitialTab} />}
            {activeTab === 'attendance' && <Attendance key={`attendance-${attendanceInitialTab}-${attendanceKey}`} initialTab={attendanceInitialTab} isAdmin={true} />}
            {activeTab === 'settings' && <AdminSettingsTab />}
          </motion.div>
        </div>

        {/* Footer */}
        <footer className="border-t border-gray-700 py-8 px-4 sm:px-6 lg:px-8 mt-8">
          <div className="max-w-7xl mx-auto text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <img 
                src="/logo.png" 
                alt="Gugan Dental Care Logo" 
                className="h-8 w-auto object-contain"
                onError={(e) => {
                  const fallback = e.target.nextElementSibling;
                  if (fallback) {
                    e.target.style.display = 'none';
                    fallback.style.display = 'block';
                  }
                }}
              />
              <Stethoscope className="h-6 w-6 text-blue-400 hidden" />
              <span className="text-lg font-semibold gradient-text">Gugan Dental Care</span>
            </div>
            <p className="text-gray-400 text-sm">
              © 2025 Gugan Dental Care. All rights reserved. Developed and maintained by{' '}
              <a 
                href="https://www.neinus.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 underline transition-colors"
              >
                Neinus Technologies
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default AdminDashboard;